import React, { useEffect, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';

import { RegisterStyles } from './style';

import { BsQuestionCircleFill } from 'react-icons/bs';
import Tooltip from 'reusable/Tooltip';
import useQuery from 'reusable/customHooks/useQuery';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useRegisterUserMutation } from 'services/api/ums';
import { track } from 'analytics/posthog';
import { passwordSchema } from '../constants';
import * as actions from '../actions';
import { connect } from 'react-redux';
import { isAuthorizedDomain } from '../helpers';
import useHeader from 'containers/App/Header/useHeader';

const redactToken = (value?: string | null) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;

const getMutationErrorMessage = (error: any) =>
  get(error, 'data.message') ||
  get(error, 'data.error') ||
  get(error, 'error') ||
  'Error Signing up user';

function RegisterPage({ loginUser }) {
  useHeader('Register');
  const [query] = useQuery();
  const history = useHistory();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const token = query.get('token');
  const email = query.get('email') || '';

  const [registerUser, registerUserState] = useRegisterUserMutation();
  const isInviteLinkValid = Boolean(email && token);
  const inviteLinkError = !isInviteLinkValid
    ? 'Registration link is invalid or incomplete. Please request a new invitation email.'
    : '';

  useEffect(() => {
    if (registerUserState.isSuccess) {
      toast.success(get(registerUserState, 'data.message'));
      loginUser({
        profileObj: {
          ...registerUserState.data,
          familyName: registerUserState.data.lastName,
          givenName: registerUserState.data.firstName,
          type: isAuthorizedDomain(registerUserState.data.email)
            ? 'internal'
            : 'external',
        },
        tokenId: registerUserState.data.accessToken,
        tokenType: 'UMS',
      });
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerUserState.isSuccess]);

  const isValidForm = () => {
    if (!firstName || !lastName || !company || !email) return false;
    if (
      !password ||
      !passwordSchema.validate(password) ||
      password !== confirmPassword
    )
      return false;
    return true;
  };

  const handleRegister = async () => {
    if (!isInviteLinkValid) {
      setError(inviteLinkError);
      console.error('UMS register blocked: invalid invitation link', {
        email,
        token: redactToken(token),
        currentUrl: window.location.href,
      });
      return;
    }

    setError('');

    const requestPayload = {
      body: {
        email,
        firstName,
        lastName,
        company,
        password,
        passwordHash: password,
      },
      params: { email, token },
    };

    track('register attempted', {
      surface: 'register',
    });

    try {
      await registerUser(requestPayload).unwrap();
    } catch (error) {
      const errorMessage = getMutationErrorMessage(error);
      const errorStatus = get(error, 'status');

      setError(errorMessage);
      console.error('UMS register submit failed', {
        request: {
          email,
          firstName,
          lastName,
          company,
          hasPassword: Boolean(password),
          token: redactToken(token),
          currentUrl: window.location.href,
        },
        response: {
          status: errorStatus,
          data: get(error, 'data'),
          error: get(error, 'error'),
        },
      });
      track('register failed', {
        error_code_or_message: errorMessage,
        status_code:
          typeof errorStatus === 'number' ? errorStatus : String(errorStatus || ''),
        surface: 'register',
      });
    }
  };

  return (
    <RegisterStyles>
      <Container fluid className="register-container">
        <Form.Group
          className={!firstName ? 'form-group' : 'form-group success'}
        >
          <Form.Label>First Name *</Form.Label>
          <Form.Control
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
        </Form.Group>
        <Form.Group className={!lastName ? 'form-group' : 'form-group success'}>
          <Form.Label>Last Name *</Form.Label>
          <Form.Control
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
        </Form.Group>
        <Form.Group className={!company ? 'form-group' : 'form-group success'}>
          <Form.Label>Company Name *</Form.Label>
          <Form.Control
            type="text"
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="form-group">
          <Form.Label>Email</Form.Label>
          <Form.Control type="text" value={email} disabled />
        </Form.Group>
        <Form.Group
          className={
            !password
              ? 'form-group'
              : passwordSchema.validate(password)
                ? 'form-group success'
                : 'form-group error'
          }
        >
          <Form.Label>
            Password *
            <Tooltip
              tooltipValue={
                <ul>
                  <li>
                    Can use letters, numbers, and special characters. No spaces.
                  </li>
                  <li>Minimum length 8 characters</li>
                  <li> At least 1 uppercase character At</li>
                  <li>least 1 lowercase character</li>
                </ul>
              }
              placement="right"
            >
              <div className="help-password">
                <BsQuestionCircleFill />
              </div>
            </Tooltip>
          </Form.Label>
          <Form.Control
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </Form.Group>
        <Form.Group
          className={
            !confirmPassword
              ? 'form-group'
              : password === confirmPassword
                ? 'form-group success'
                : 'form-group error'
          }
        >
          <Form.Label>Confirm Password *</Form.Label>
          <Form.Control
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
        </Form.Group>
        {(error || inviteLinkError) && (
          <div className="error-message">{error || inviteLinkError}</div>
        )}
        <Button
          disabled={
            !isInviteLinkValid || !isValidForm() || registerUserState.isLoading
          }
          onClick={handleRegister}
        >
          Register
        </Button>
      </Container>
    </RegisterStyles>
  );
}

const mapDispatchToProps = dispatch => ({
  loginUser: data => dispatch(actions.loginUser(data)),
});

export default connect(null, mapDispatchToProps)(RegisterPage);
